import React, { useState, useRef, useEffect } from "react";
import { motion } from "motion/react";
import { cva, type VariantProps } from "class-variance-authority";
import { currencyFormatter } from "../helpers/index";

interface AnimatedCounterProps {
  count: number;
  variant?: "default" | "currency";
  className?: string;
  animationConfig?: {
    startOffset?: number;
    initialDelay?: number;
    slowestDelay?: number;
    delayIncrement?: number;
    fastTicksCount?: number;
    finalDelay?: number;
  };
}

export const AnimatedCounter: React.FC<AnimatedCounterProps> = ({
  count,
  variant = "default",
  className = "",
  animationConfig = {},
}) => {
  const {
    startOffset = 15,
    initialDelay = 60,
    slowestDelay = 200,
    delayIncrement = 10,
    fastTicksCount = 10,
    finalDelay = 320,
  } = animationConfig;

  const [displayedCount, setDisplayedCount] = useState(
    Math.max(0, count - startOffset)
  );
  const [animationDirection, setAnimationDirection] = useState(1);
  const isAnimatingRef = useRef(false);

  const startCountAnimation = () => {
    if (isAnimatingRef.current) return;
    isAnimatingRef.current = true;
    setAnimationDirection(1);

    let currentDelay = initialDelay;
    let tickCount = 0;

    const incrementCounter = () => {
      setDisplayedCount((currentValue) => {
        const nextValue = currentValue + 1;
        if (nextValue >= count) {
          isAnimatingRef.current = false;
          return count;
        }
        return nextValue;
      });

      tickCount++;
      if (displayedCount + 1 >= count) {
        return;
      }

      if (tickCount < fastTicksCount) {
        setTimeout(incrementCounter, currentDelay);
      } else {
        currentDelay = Math.min(slowestDelay, currentDelay + delayIncrement);
        if (currentDelay < slowestDelay) {
          setTimeout(incrementCounter, currentDelay);
        } else {
          setTimeout(incrementCounter, finalDelay);
        }
      }
    };
    incrementCounter();
  };

  useEffect(() => {
    startCountAnimation();
  }, []);

  return (
    <NumberTicker
      value={displayedCount}
      direction={animationDirection}
      variant={variant}
      className={className}
    />
  );
};

interface NumberTickerProps {
  value: number;
  direction?: number;
  variant?: "default" | "currency";
  className?: string;
}

interface DigitProps extends VariantProps<typeof digitContainerVariants> {
  prev: string;
  next: string;
  dir: number;
}

// CVA variant configurations
const digitContainerVariants = cva("ui-relative ui-inline-block", {
  variants: {
    variant: {
      default: "ui-w-[0.65em] ui-h-[1.2em]",
      currency: "ui-w-[1.8ch] ui-h-[2em] ui-overflow-visible",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

const digitTextVariants = cva("ui-absolute", {
  variants: {
    variant: {
      default:
        "ui-inset-0 ui-text-xxl ui-bg-gradient-to-r ui-from-yay-darker ui-via-yay-darker/5 ui-to-yay-dark ui-bg-clip-text ui-text-transparent ui-font-bold ui-flex ui-items-center ui-justify-center",
      currency:
        "ui-text-display-sm-semibold ui-left-0 ui-right-0 ui-text-center",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

const NumberTicker: React.FC<NumberTickerProps> = ({
  value,
  direction = 1,
  variant = "default",
  className = "",
}) => {
  const prevValue = useRef(value);

  const prevLength = String(prevValue.current).length;
  const currentLength = String(value).length;
  const padLength = Math.max(prevLength, currentLength);

  const prevDigits = String(prevValue.current)
    .padStart(padLength, "0")
    .split("");
  const newDigits = String(value).padStart(padLength, "0").split("");

  useEffect(() => {
    prevValue.current = value;
  }, [value]);

  return (
    <div className={`flex flex-row ${className}`}>
      {newDigits.map((digit, i) => (
        <Digit
          key={i}
          prev={prevDigits[i] ?? ""}
          next={digit}
          dir={direction}
          variant={variant}
        />
      ))}
    </div>
  );
};

const Digit: React.FC<DigitProps> = ({ prev, next, dir, variant }) => {
  const hasChanged = prev !== next;
  const fromY = dir === 1 ? "40%" : "-40%";
  const toY = dir === 1 ? "-40%" : "40%";

  const formatDigit = (digit: string) => {
    return variant === "currency" ? currencyFormatter(digit, false) : digit;
  };

  return (
    <span className={digitContainerVariants({ variant })}>
      {!hasChanged && (
        <span className={digitTextVariants({ variant })}>
          {formatDigit(next)}
        </span>
      )}

      {hasChanged && (
        <>
          <motion.span
            key={`old-${prev}`}
            className={digitTextVariants({ variant })}
            initial={{ y: "0%", filter: "blur(0px)" }}
            animate={{ y: toY, opacity: 0, filter: "blur(6px)" }}
            transition={{
              type: "spring",
              stiffness: 180,
              damping: 20,
              mass: 0.5,
            }}
          >
            {formatDigit(prev)}
          </motion.span>

          <motion.span
            key={`new-${next}`}
            className={digitTextVariants({ variant })}
            initial={{ y: fromY, opacity: 0, filter: "blur(6px)" }}
            animate={{ y: "0%", opacity: 1, filter: "blur(0px)" }}
            transition={{
              type: "spring",
              stiffness: 180,
              damping: 20,
              mass: 0.5,
            }}
          >
            {formatDigit(next)}
          </motion.span>
        </>
      )}
    </span>
  );
};
